import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Mail, Lock, User, Building2, Phone, MapPin, Eye, EyeOff, ArrowRight, Loader2, Check } from 'lucide-react';

const ATFF_LOGO = "https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/0f6s53y1_Untitled%20design%20%285%29.png";

const countries = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Morocco', 'Tanzania', 
  'Ethiopia', 'Rwanda', 'Senegal', "Cote d'Ivoire", 'Cameroon', 'Uganda', 
  'Zimbabwe', 'Botswana', 'Namibia', 'Tunisia', 'Algeria', 'Angola', 'Benin'
];

const userTypes = [
  { value: 'sme', label: 'SME / Business Owner' },
  { value: 'buyer', label: 'Buyer / Importer' },
  { value: 'logistics', label: 'Logistics Provider' },
  { value: 'finance', label: 'Financial Institution' },
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, googleLogin, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
    phone: '',
    country: '',
    user_type: 'sme',
  });

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(formData);
      toast.success('Account created successfully!');
      // Redirect based on user type
      if (formData.user_type === 'buyer') {
        navigate('/buyer/onboarding');
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const userData = await googleLogin(credentialResponse.credential);
      if (userData) {
        toast.success('Account created successfully!');
        // Use window.location for a full page refresh to ensure auth state is loaded
        window.location.href = '/onboarding';
      }
    } catch (error) {
      console.error('Google signup error:', error);
      toast.error('Google sign up failed. Please try again.');
    }
  };

  const handleGoogleError = () => {
    toast.error('Google sign up failed. Please try again.');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-lg shadow-xl border-0" data-testid="register-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4">
              <img 
                src={ATFF_LOGO} 
                alt="ATFF" 
                className="h-20 md:h-24 w-auto object-contain mx-auto"
              />
            </div>
            <CardTitle className="text-2xl font-poppins">Create Account</CardTitle>
            <CardDescription>Join ATFFtrade.africa and start trading across Africa</CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Google Register */}
            <div className="flex justify-center mb-6">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                theme="outline"
                size="large"
                text="signup_with"
                shape="rectangular"
                width="100%"
                data-testid="google-register-btn"
              />
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">or register with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="name"
                      name="name"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10 h-12"
                      required
                      data-testid="name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="company_name"
                      name="company_name"
                      placeholder="Your company"
                      value={formData.company_name}
                      onChange={handleChange}
                      className="pl-10 h-12"
                      data-testid="company-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 h-12"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+234..."
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10 h-12"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label>Country</Label>
                  <Select onValueChange={(v) => handleSelectChange('country', v)} data-testid="country-select">
                    <SelectTrigger className="mt-1 h-12">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Account Type</Label>
                <Select 
                  value={formData.user_type} 
                  onValueChange={(v) => handleSelectChange('user_type', v)}
                  data-testid="user-type-select"
                >
                  <SelectTrigger className="mt-1 h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {userTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10 h-12"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">By registering, you agree to:</p>
                <ul className="space-y-1">
                  {['Terms of Service', 'Privacy Policy', 'AfCFTA Trade Guidelines'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-gray-500">
                      <Check className="w-4 h-4 text-continental" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                type="submit"
                className="w-full h-12 btn-primary"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <p className="text-center mt-6 text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-continental font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
